import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import NextHead from 'next/head';
import TagManager from 'react-gtm-module';

import { head } from '@pik/pik-utils';

const { constants, createFaviconLink, checkDarkMode } = head;

const isProd = process.env.NODE_ENV === 'production';

const CTM_ID_PIK_GROUP = 'GTM-58ZH9TP';

const renderTag = (i, data, isLink, isDark) => {
  const key = `${Object.values(data)[0]}_${i}_${Number(isDark)}`;
  const { createUrl, href, content } = data;
  let newData = data;

  if (isLink) {
    if (createUrl) {
      newData = {
        ...data,
        href: createFaviconLink(href, isDark),
      };
    }

    return (
      <link
        key={key}
        {...newData}
      />
    );
  }

  if (createUrl) {
    newData = {
      ...data,
      content: createFaviconLink(content, isDark),
    };
  }

  return (
    <meta
      key={key}
      {...newData}
    />
  );
};

const renderNoScript = (noscript) => {
  if (noscript) return <noscript key={noscript}>{noscript}</noscript>;
  return null;
};

const renderScriptSrc = (scriptSrc) => {
  if (scriptSrc) return <script key={scriptSrc} src={scriptSrc} async />;
  return null;
};

const renderCounter = (counter) => {
  const { script, noscript, scriptSrc } = counter;

  if (isProd) {
    return (
      <>
        <script dangerouslySetInnerHTML={{ __html: script }} />
        {renderNoScript(noscript)}
        {renderScriptSrc(scriptSrc)}
      </>
    );
  }

  return null;
};

const renderCanonical = (queryLang, canonicalUrl) => {
  if (queryLang) {
    return <link rel="canonical" href={`https://www.pik-group.com${canonicalUrl}`} />;
  }
  return null;
};

const Head = ({
  data: {
    title, description, keywords, ogImage,
  }, canonicalUrl, queryLang,
}) => {
  const [isDark, darkHandler] = useState(false);

  useEffect(() => {
    darkHandler(checkDarkMode());
    if (isProd) {
      TagManager.initialize({ gtmId: CTM_ID_PIK_GROUP });
    }
  }, []);

  return (
    <NextHead>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {ogImage && <meta name="og:image" content={ogImage} />}
      {renderCanonical(queryLang, canonicalUrl)}
      {constants.meta.map((data, i) => renderTag(i, data, false, isDark))}
      {constants.link.map((data, i) => renderTag(i, data, true, isDark))}
    </NextHead>
  );
};

Head.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.string,
    ogImage: PropTypes.string,
  }).isRequired,
  canonicalUrl: PropTypes.string,
  queryLang: PropTypes.string,
};

Head.defaultProps = {
  canonicalUrl: null,
  queryLang: null,
};

export default Head;
